import { useFocusEffect, useIsFocused, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	duelsCurrentRefresh,
	duelsHubRequest,
	duelsJoinErrorDismiss,
	duelsJoinRequest,
	duelsPlayResume,
	duelsPlayStop,
	duelsReconnectRequest,
	duelsResultOpened,
	duelsScreenMounted,
	duelsScreenUnmounted,
} from '../../store/duels/duelsActions';
import {
	selectCurrentDuel,
	selectDuelsConnection,
	selectDuelsHub,
	selectDuelsHubErrorKey,
	selectDuelsJoinErrorKey,
	selectDuelsJoining,
	selectDuelSnapshot,
	selectPendingResultDuelId,
	selectPlayingDuelId,
} from '../../store/duels/duelsReducer';
import {
	DuelDivisionCard,
	DuelHero,
	DuelHistoryShortcut,
	DuelInProgressCard,
	DuelSeasonCard,
} from '../Components/Duels/DuelHubCards';
import { duelText } from '../Components/Duels/duelsText';
import { DuelLoading, DuelNotice, DuelsPage, duelTextStyles } from '../Components/Duels/DuelsUi';
import GameTable from '../Components/Game/GameTable';
import { SocketPayload } from '../Components/Game/types';
import { RootStackParamList } from '../navigator';

const DUEL_GAME_LEGACY_ROOM_ID = 5;
const DUEL_GAME_ROOM_SLUG = 'vip';
const PREPARING_LIMIT_MS = 30000;

type NavType = NativeStackNavigationProp<RootStackParamList>;

const DuelsScreen = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigation = useNavigation<NavType>();

	const connection = useSelector(selectDuelsConnection);
	const hub = useSelector(selectDuelsHub);
	const hubErrorKey = useSelector(selectDuelsHubErrorKey);
	const current = useSelector(selectCurrentDuel);
	const joining = useSelector(selectDuelsJoining);
	const joinErrorKey = useSelector(selectDuelsJoinErrorKey);
	const playingDuelId = useSelector(selectPlayingDuelId);
	const playing = useSelector(selectDuelSnapshot(playingDuelId));
	const pendingResultDuelId = useSelector(selectPendingResultDuelId);
	const [preparingTooLong, setPreparingTooLong] = useState(false);
	const isFocused = useIsFocused();

	useEffect(() => {
		dispatch(duelsScreenMounted());
		return () => {
			dispatch(duelsScreenUnmounted());
		};
	}, [dispatch]);

	useFocusEffect(
		useCallback(() => {
			dispatch(duelsHubRequest());
		}, [dispatch]),
	);

	useEffect(() => {
		if (!pendingResultDuelId || !isFocused) return;
		dispatch(duelsResultOpened());
		navigation.navigate('DuelResult', { duelId: pendingResultDuelId });
	}, [dispatch, isFocused, navigation, pendingResultDuelId]);

	const tableId = playing?.currentPlayerTableId ?? null;

	useEffect(() => {
		setPreparingTooLong(false);
		if (!playingDuelId || tableId) return;
		const timer = setTimeout(() => setPreparingTooLong(true), PREPARING_LIMIT_MS);
		return () => clearTimeout(timer);
	}, [playingDuelId, tableId]);

	const handleGameEvent = useCallback(
		(event: string, _data?: SocketPayload) => {
			if (event === 'goToLobby' || event === 'tableDoesNotExist') {
				dispatch(duelsPlayStop());
				dispatch(duelsHubRequest());
			}
		},
		[dispatch],
	);

	const handleBack = useCallback(() => {
		if (navigation.canGoBack()) navigation.goBack();
		else navigation.navigate('Main');
	}, [navigation]);

	if (playingDuelId && tableId) {
		return (
			<View style={styles.table}>
				<GameTable
					key={tableId}
					tableId={tableId}
					legacyRoomId={DUEL_GAME_LEGACY_ROOM_ID}
					roomSlug={DUEL_GAME_ROOM_SLUG}
					isTournamentRoom={false}
					isDuel
					onGameEvent={handleGameEvent}
				/>
			</View>
		);
	}

	return (
		<DuelsPage title={duelText(t, 'T_SITE_DUELS_TITLE')} onBack={handleBack}>
			{({ width, scale }) => {
				if (playingDuelId) {
					return (
						<DuelNotice
							scale={scale}
							width={width}
							text={duelText(
								t,
								preparingTooLong ? 'T_SITE_DUELS_PREPARING_SLOW' : 'T_SITE_DUELS_PREPARING',
							)}
							actionLabel={duelText(
								t,
								preparingTooLong ? 'T_SITE_DUELS_RETRY' : 'T_SITE_DUELS_BACK_TO_DUELS',
							)}
							onAction={() => {
								if (preparingTooLong) {
									setPreparingTooLong(false);
									dispatch(duelsCurrentRefresh());
								} else {
									dispatch(duelsPlayStop());
								}
							}}
						/>
					);
				}

				return (
					<>
						{connection === 'unavailable' ? (
							<DuelNotice
								scale={scale}
								width={width}
								text={duelText(t, 'T_SITE_DUELS_ERROR_UNAVAILABLE')}
							/>
						) : null}
						{connection === 'replaced' ? (
							<DuelNotice
								scale={scale}
								width={width}
								text={duelText(t, 'T_SITE_DUELS_OPENED_ELSEWHERE')}
								actionLabel={duelText(t, 'T_SITE_DUELS_RECONNECT')}
								onAction={() => dispatch(duelsReconnectRequest())}
							/>
						) : null}

						{current ? (
							<DuelInProgressCard
								width={width}
								scale={scale}
								duel={current}
								onViewResult={() => navigation.navigate('DuelResult', { duelId: current.id })}
								onResume={() => dispatch(duelsPlayResume(current.id))}
							/>
						) : (
							<DuelHero
								width={width}
								scale={scale}
								finding={joining}
								disabled={connection !== 'connected'}
								onFind={() => dispatch(duelsJoinRequest())}
							/>
						)}

						{joinErrorKey ? (
							<DuelNotice
								scale={scale}
								width={width}
								text={duelText(t, joinErrorKey)}
								actionLabel={duelText(t, 'T_SITE_DUELS_OK')}
								onAction={() => dispatch(duelsJoinErrorDismiss())}
							/>
						) : null}

						{connection === 'connecting' && !current ? (
							<Text style={[duelTextStyles.muted, { fontSize: 12 * scale }]}>
								{duelText(t, 'T_SITE_DUELS_CONNECTING')}
							</Text>
						) : null}

						{hub ? (
							<>
								<DuelDivisionCard
									width={width}
									scale={scale}
									rating={hub.rating}
									onPress={() => navigation.navigate('Ranking', { category: 'duels' })}
								/>
								<DuelSeasonCard
									width={width}
									scale={scale}
									season={hub.season}
									onPress={() => navigation.navigate('DuelSeason')}
								/>
							</>
						) : hubErrorKey ? (
							<DuelNotice
								scale={scale}
								width={width}
								text={duelText(t, hubErrorKey)}
								actionLabel={duelText(t, 'T_SITE_DUELS_RETRY')}
								onAction={() => dispatch(duelsHubRequest())}
							/>
						) : (
							<DuelLoading />
						)}

						<DuelHistoryShortcut
							width={width}
							scale={scale}
							onPress={() => navigation.navigate('DuelHistory')}
						/>
					</>
				);
			}}
		</DuelsPage>
	);
};

const styles = StyleSheet.create({
	table: {
		flex: 1,
		backgroundColor: '#000',
	},
});

export default DuelsScreen;
